/**
 * Factory for EXTERNAL providers (validated link): Zoom / Meet / custom.
 *
 * They don't embed: they return an `external` `JoinDescriptor` with a validated
 * https URL. The URL comes from the session (`meetingUrl`, a per-session link set by
 * the teacher) or, failing that, from the provider's config (`url`, e.g. a
 * fixed personal room). `buildJoin` is pure and throws if the URL isn't valid https.
 */

import {
  type JoinDescriptor,
  type JoinUser,
  type LiveSession,
  type MeetingProvider,
  type MeetingProviderManifest,
  validateHttpsUrl,
} from "../registry";

export interface ExternalProviderOptions {
  id: string;
  name: string;
  description: string;
  requiresCredentials?: boolean;
  urlPlaceholder?: string;
}

/** Creates a standard external provider (Zoom/Meet/custom share the logic). */
export function makeExternalProvider(opts: ExternalProviderOptions): MeetingProvider {
  const manifest: MeetingProviderManifest = {
    id: opts.id,
    name: opts.name,
    description: opts.description,
    capabilities: {
      embeddable: false,
      attendanceCallback: false,
      recordingFetch: false,
      requiresCredentials: opts.requiresCredentials ?? false,
    },
    configSchema: [
      {
        key: "url",
        label: "URL de la reunión",
        type: "url",
        required: false,
        placeholder: opts.urlPlaceholder,
        help: "Enlace fijo (sala personal). Si la sesión trae su propio enlace, ese tiene prioridad.",
      },
    ],
  };

  return {
    manifest,
    buildJoin(session: LiveSession, cfg: Record<string, unknown>, _user: JoinUser): JoinDescriptor {
      const fromSession = session.meetingUrl?.trim();
      const fromCfg = typeof cfg.url === "string" ? cfg.url.trim() : "";
      const raw = fromSession || fromCfg;
      return {
        mode: "external",
        provider: opts.id,
        url: validateHttpsUrl(raw),
      };
    },
  };
}
